import React, { useState } from 'react';
import styled from 'styled-components';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import TotalModalWrapper from '../../Model/Share/Normal/TotalModalWrapper';

import { ReactComponent as ViewOnIcon } from '../../image/ViewOn.svg';
import { ReactComponent as ViewOffIcon } from '../../image/ViewOff.svg';

export default function MainPasswordEditModal(props) {
  const { onClickExit, onClickSave } = props;

  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [isViewCurrent, setIsViewCurrent] = useState(false);
  const [isViewNew, setIsViewNew] = useState(false);
  const [isViewConfirm, setIsViewConfirm] = useState(false);

  const [currentError, setCurrentError] = useState('');
  const [newError, setNewError] = useState('');
  const [confirmError, setConfirmError] = useState('');

  // 영문+숫자+특수문자 포함 6자 이상 //
  function isValidPasswordFormat(pw) {
    const v = (pw || '').trim();
    const hasLetter = /[a-zA-Z]/.test(v);
    const hasNumber = /[0-9]/.test(v);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>\-_=+\[\]\\;'`~]/.test(v);
    return hasLetter && hasNumber && hasSpecial && v.length >= 6;
  }

  // 부모가 API 호출 후 성공 시 모달을 닫음 //
  function onClickSaveButton() {
    setCurrentError('');
    setNewError('');
    setConfirmError('');

    if (currentPassword === '') {
      setCurrentError(t('myStoreSetting.account.passwordEditModal.currentEmpty'));
      return;
    }
    if (newPassword === '') {
      setNewError(t('myStoreSetting.account.passwordEditModal.newEmpty'));
      return;
    }
    if (!isValidPasswordFormat(newPassword)) {
      setNewError(t('myStoreSetting.account.passwordEditModal.newFormatError'));
      return;
    }
    if (newPassword === currentPassword) {
      setNewError(t('myStoreSetting.account.passwordEditModal.samePassword'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setConfirmError(t('myStoreSetting.account.passwordEditModal.confirmMismatch'));
      return;
    }

    if (onClickSave) {
      onClickSave(currentPassword, newPassword);
      return;
    }
    onClickExit();
  }

  // 한 필드 렌더 //
  function renderField(label, value, setValue, isView, setIsView, errorMessage) {
    return (
      <FieldGroup>
        <Text
          fontFamily='NotoSansKR-Medium'
          fontSize={14}
          color={theme.text}>
          {label}
        </Text>
        <InputBox borderColor={theme.line} backgroundColor={theme.subBackground}>
          <InputField
            color={theme.text}
            placeholderColor={theme.bottomText}
            placeholder={t('myStoreSetting.account.passwordEditModal.placeholder')}
            type={isView ? 'text' : 'password'}
            value={value}
            onChange={(e) => setValue(e.target.value)} />
          {
            isView ?
              <ViewOffEye color={theme.line} onClick={() => setIsView(!isView)} />
              :
              <ViewOnEye color={theme.line} onClick={() => setIsView(!isView)} />
          }
        </InputBox>
        {
          errorMessage &&
          <Text
            fontFamily='NotoSansKR-Regular'
            fontSize={12}
            color={theme.pointRed}>
            {errorMessage}
          </Text>
        }
      </FieldGroup>
    );
  }

  return (
    <TotalModalWrapper>
      <Modal backgroundColor={theme.subBackground}>
        <TitleWrapper>
          <Text
            fontFamily='NotoSansKR-Bold'
            fontSize={16}
            color={theme.text}>
            {t('myStoreSetting.account.passwordEditModal.title')}
          </Text>
          <Text
            fontFamily='NotoSansKR-Regular'
            fontSize={12}
            color={theme.gray}>
            {t('myStoreSetting.account.passwordEditModal.description')}
          </Text>
        </TitleWrapper>
        {renderField(t('myStoreSetting.account.passwordEditModal.currentLabel'), currentPassword, setCurrentPassword, isViewCurrent, setIsViewCurrent, currentError)}
        {renderField(t('myStoreSetting.account.passwordEditModal.newLabel'), newPassword, setNewPassword, isViewNew, setIsViewNew, newError)}
        {renderField(t('myStoreSetting.account.passwordEditModal.confirmLabel'), confirmPassword, setConfirmPassword, isViewConfirm, setIsViewConfirm, confirmError)}
        <ButtonRow>
          <CloseButton backgroundColor={theme.buttonFill} onClick={onClickExit}>
            <Text
              fontFamily='NotoSansKR-Medium'
              fontSize={14}
              color={theme.text}>
              {t('myStoreSetting.account.passwordEditModal.close')}
            </Text>
          </CloseButton>
          <SaveButton backgroundColor={theme.primary} onClick={onClickSaveButton}>
            <Text
              fontFamily='NotoSansKR-Medium'
              fontSize={14}
              color={theme.subBackground}>
              {t('myStoreSetting.account.passwordEditModal.save')}
            </Text>
          </SaveButton>
        </ButtonRow>
      </Modal>
    </TotalModalWrapper>
  );
}

const Modal = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 10px;

  width: 330px;
  padding: 20px;
  box-sizing: border-box;

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 10px;

  padding-bottom: 20px;

  width: 100%;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 8px;

  padding-bottom: 10px;

  width: 100%;
`;

const InputBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  gap: 8px;

  width: 100%;
  height: 38px;
  padding: 0 10px;
  box-sizing: border-box;

  border-radius: 5px;
  border: 1px solid ${props => props.borderColor};
  background-color: ${props => props.backgroundColor};
`;

const InputField = styled.input`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  outline: none;
  border: 0px;

  flex: 1 0 0;
  min-width: 0;
  height: 100%;

  color: ${props => props.color};
  background-color: transparent;

  ::placeholder {
    color: ${props => props.placeholderColor};
  }
`;

const ViewOnEye = styled(ViewOnIcon)`
  width: 20px;
  height: auto;

  flex-shrink: 0;

  path {
    stroke: ${props => props.color};
  }

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const ViewOffEye = styled(ViewOffIcon)`
  width: 20px;
  height: auto;

  flex-shrink: 0;

  path {
    fill: ${props => props.color};
  }

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: stretch;
  align-items: center;

  gap: 8px;

  width: 100%;

  margin-top: 40px;
`;

const CloseButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 72px;
  height: 44px;

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const SaveButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  flex: 1 0 0;
  height: 44px;

  border-radius: 5px;
  background-color: ${props => props.backgroundColor};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};
  background-color: ${props => props.backgroundColor || 'transparent'};

  margin: 0 0 0 0;
`;
